// Device-local participant covariates, read once at session start and stamped onto the
// consent record + every trace — the same read-once pattern as sourceTag / revealArm.
// Design, power arithmetic and governance: `analysis/skill_covariate_proposal.md`.
//
// WHY: every open question about the dTA↔cTA differences hits the same wall — we cannot
// separate "our audience is different" from "unpaid changes behaviour". A per-participant
// ability measure attacks that from the inside, and an in-the-wild platform can carry
// population covariates a paid panel structurally cannot.
//
// NO ACCOUNT, NO ID, NO RAW VALUES. Everything here is a coarse band read from localStorage,
// the pattern already cleared for Targets' `exposure_band` and covered by
// nerdle-static/privacy.html §1.2 ("locally saved game statistics … to support the
// de-identified research described in section 2"). Works for logged-out players — ~94% of
// labs participants — which the account-side route never could.
//
// CUT-POINTS ARE SHARED WITH shuffle/src/lib/labsCapture.ts AND MUST NOT DRIFT. They are
// percentiles of the emailed-invitee pool (n = 1349, 2026-09-04). Identical values on both
// surfaces are what make a band mean the same thing in labs and in Targets; changing them
// here alone would silently break every cross-surface comparison.

/**
 * ONE classic-nerdle game, copied verbatim from `statsState.lastGame` (written by the classic
 * app — see nerdle/src/lib/storage.ts, StoredLastGame).
 *
 * WHY RAW, WHEN EVERYTHING ELSE HERE IS BANDED. The intended skill measure scores each guess
 * against WHAT OTHER PLAYERS DID ON THE SAME PUZZLE THAT DAY — an empirical within-puzzle norm,
 * which needs no optimal solver and no puzzle generator. That norm cannot exist at capture time:
 * it is built later from the whole logged-in classic population. So there is no band to compute
 * here, and banding anything now would throw the measure away.
 *
 * WHY THIS IS CAPTURE-OR-LOSE. localStorage holds TODAY'S game only and overwrites it tomorrow.
 * ~94% of labs participants play logged out, so for most of them this session is the single
 * moment the data is reachable. Account-linked participants are recoverable retrospectively from
 * the cloud; these are not.
 *
 * WHY NOT THE WHOLE STATS OBJECT. `gamesPlayed` plus the six-element `winRows` vector is very
 * probably unique across the player base — a quasi-identifier that would let anyone with
 * ActorsTable access re-identify the account from a trace, breaching the account-linking rule in
 * substance while storing no id. The cumulative picture is already here, safely, as skillBand /
 * tenureBand. One game on a puzzle thousands of people played the same day is a different risk.
 *
 * RELEASE HANDLING: treat like `source.token` — STRIP OR COARSEN `lastGame` in any released
 * dataset. An unusual guess sequence is a weak re-identification path for ORGANIC participants,
 * who have no token and were otherwise unlinkable.
 */
export type LastClassicGame = {
  i: number // solutionIndex — which puzzle, so it can be normed against other players
  s: string // the solution — needed to score a LOSS, where it is not in the guess list
  g: string[] // guesses in order
  t: (number | null)[] // epoch ms per guess, INDEX-ALIGNED with g; null = made in an
  // earlier session before the page loaded, so it could not be timed. Never read as 0.
  tLoad: number
  tStart: number | null // epoch ms the splash closed; null if it never showed
  splashShown: boolean // read this; do NOT infer it from tStart — see nerdle App.tsx
}

export type NerdleSkill = {
  skillBand: number | null // 1 = strongest (fewest guesses) … 12; null = not measurable
  tenureBand: number | null // 1 = lightest … 12; null = not measurable
  statsAgeBand: string | null // fresh | <90d | <365d | stale
  statsSource: string // statsState | deviceStatsState | none | too-few-games | unparseable
  g24Prior: string // 0 | 1-2 | 3+  — prior days in the Targets 24-mirror arm
  targetsExposure: string // new | 1-4 | 5-19 | 20-99 | 100+  — Targets L1 tenure
  lastGame: LastClassicGame | null // today's classic game, if this device played it
}

const SKILL_CUTS = [3.27, 3.35, 3.42, 3.49, 3.56, 3.65, 3.73, 3.83, 3.94, 4.12, 4.37]
const TENURE_CUTS = [12, 15, 19, 27, 38, 57, 82, 119, 222, 414, 692]
const MIN_GAMES = 10 // below this the mean-guess estimate is noise, not skill

const bandOf = (v: number, cuts: number[]): number => {
  for (let i = 0; i < cuts.length; i++) if (v <= cuts[i]) return i + 1
  return cuts.length + 1
}

const ageBand = (lastUpdated: unknown): string | null => {
  if (typeof lastUpdated !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(lastUpdated)) return null
  const days = (Date.now() - Date.parse(lastUpdated.slice(0, 10))) / 86400000
  if (!isFinite(days) || days < 0) return null
  return days < 30 ? 'fresh' : days < 90 ? '<90d' : days < 365 ? '<365d' : 'stale'
}

// Targets L1 tenure, cut identically to shuffle's exposureBand() so labs participants and
// Targets players are directly comparable on it. Targets *skill* is not recoverable at all
// (see the proposal §2) but tenure is, and it is the one axis on which the two populations
// in the reactivity comparison can be matched.
const exposureBand = (n: number): string =>
  n <= 0 ? 'new' : n < 5 ? '1-4' : n < 20 ? '5-19' : n < 100 ? '20-99' : '100+'

/**
 * Validate and copy `statsState.lastGame`. Shape-checked rather than trusted: this is data from
 * another app's storage on the user's own device, so a stale or hand-edited blob must yield null
 * rather than a malformed record in the corpus. Arrays are copied and length-capped so a corrupt
 * blob cannot bloat a trace.
 */
const MAX_GUESSES = 12 // classic allows 6; the cap is only a guard against a corrupt blob

const readLastGame = (raw: any): LastClassicGame | null => {
  if (!raw || typeof raw !== 'object') return null
  const g = Array.isArray(raw.g) ? raw.g.filter((x: any) => typeof x === 'string') : null
  // Map rather than filter: filtering would drop nulls and silently shift every later
  // timestamp onto the wrong guess. Alignment with `g` is the whole point.
  const t = Array.isArray(raw.t)
    ? raw.t.map((x: any) => (typeof x === 'number' && isFinite(x) ? x : null))
    : []
  if (!g || !g.length || typeof raw.s !== 'string' || !raw.s) return null
  const i = Number(raw.i)
  const tLoad = Number(raw.tLoad)
  if (!isFinite(i) || !isFinite(tLoad)) return null
  return {
    i,
    s: raw.s,
    g: g.slice(0, MAX_GUESSES),
    t: t.slice(0, MAX_GUESSES),
    tLoad,
    // null is meaningful (splash never shown) - do not coerce to 0 or to tLoad
    tStart: typeof raw.tStart === 'number' && isFinite(raw.tStart) ? raw.tStart : null,
    splashShown: raw.splashShown === true,
  }
}

const readJSON = (key: string): any => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/* ------------------------------------------------------------------------------------------
 * APP → BROWSER HANDOFF
 *
 * The nerdle app captures nerdlegame.com/* and its WebView cannot record, so in-app
 * participants are sent to their real browser (see BrowserHandoff.tsx). That move costs the
 * covariate: measured on one device 2026-09-09, the app's origin store held statsState with
 * gamesPlayed 1337 and 108 keys, while Safari's held NO statsState and 60 keys. Same person,
 * same phone, same origin — different stores. Without a handoff, every ejected participant
 * arrives as statsSource 'none'.
 *
 * WHAT TRAVELS: the OUTPUT of getNerdleSkill(), not the raw stats object. Shipping
 * `gamesPlayed` plus the six-element `winRows` vector would be a quasi-identifier (see the note
 * on LastClassicGame above); coarse bands plus one game is the disclosure already sanctioned.
 *
 * WHERE IT TRAVELS: the URL FRAGMENT, never the query. Fragments are not sent to servers, so
 * the payload stays out of access logs — it contains `lastGame`, a weak re-identifier.
 *
 * WHERE IT LANDS: sessionStorage under a labs-owned key, NEVER back into `statsState`. Writing
 * it there would clobber the player's real browser stats (merging two different play histories,
 * so wrong games-played and wrong streak) and would erase the distinction between a value read
 * from the device and one that arrived through a user-editable URL. sessionStorage rather than
 * localStorage because the handoff is for THIS session: persisting it would let a later organic
 * browser visit silently inherit stale app-derived covariates.
 * ---------------------------------------------------------------------------------------- */

export const HANDOFF_KEY = 'labsHandoffSkill'

const b64uEncode = (s: string): string =>
  btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

const b64uDecode = (s: string): string => {
  let t = s.replace(/-/g, '+').replace(/_/g, '/')
  while (t.length % 4) t += '='
  return decodeURIComponent(escape(atob(t)))
}

const MAX_HANDOFF = 4096 // guard: a corrupt or hostile fragment cannot fill sessionStorage

/**
 * Serialise this device's covariates for the handoff URL. Called in the app, before ejecting.
 */
export const encodeHandoff = (skill: NerdleSkill): string => b64uEncode(JSON.stringify(skill))

/**
 * Read `#h=…` if present, store it, and STRIP IT FROM THE URL so the payload does not linger in
 * history or get copy-pasted into a share. Runs at module load — before the games' module-level
 * `getNerdleSkill()` calls — so ordering cannot go wrong.
 */
const captureHandoffFromUrl = (): void => {
  try {
    if (typeof window === 'undefined') return
    const m = (window.location.hash || '').match(/[#&]h=([^&]+)/)
    if (!m) return
    const raw = decodeURIComponent(m[1])
    if (raw.length > MAX_HANDOFF) return
    const parsed = JSON.parse(b64uDecode(raw))
    if (!parsed || typeof parsed !== 'object') return
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(parsed))
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
  } catch {
    /* a malformed handoff is simply ignored — the session proceeds with no covariate */
  }
}

const readHandoff = (): NerdleSkill | null => {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY)
    if (!raw) return null
    const h = JSON.parse(raw)
    if (!h || typeof h !== 'object') return null
    return {
      skillBand: typeof h.skillBand === 'number' ? h.skillBand : null,
      tenureBand: typeof h.tenureBand === 'number' ? h.tenureBand : null,
      statsAgeBand: typeof h.statsAgeBand === 'string' ? h.statsAgeBand : null,
      // Provenance is the point: this value crossed a URL the participant could have edited,
      // so it must never be indistinguishable from a direct device read in the analysis.
      statsSource: 'app-handoff',
      g24Prior: typeof h.g24Prior === 'string' ? h.g24Prior : '0',
      targetsExposure: typeof h.targetsExposure === 'string' ? h.targetsExposure : 'new',
      lastGame: readLastGame(h.lastGame),
    }
  } catch {
    return null
  }
}

captureHandoffFromUrl()

/**
 * Prior days this device spent in the Targets 24-mirror arm, from the `nvG24_<date>` keys
 * shuffle writes to localStorage. Labs shares an origin with Targets, so this is readable
 * here — and it matters: a labs 24-game participant who has already met the same puzzles
 * in Targets is CROSS-CONTAMINATED for the reactivity comparison, and until now that was
 * invisible. Today's key is excluded so the field describes prior exposure only.
 */
const g24PriorBand = (): string => {
  try {
    const today = new Date().toISOString().slice(0, 10)
    let n = 0
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith('nvG24_') || k === 'nvG24_' + today || k === 'nvG24Force') continue
      if (readJSON(k)?.inArm) n++
    }
    return n === 0 ? '0' : n <= 2 ? '1-2' : '3+'
  } catch {
    return '0'
  }
}

/**
 * NOTE the null discipline: an unmeasurable band is `null`, never 0 or -1. A sentinel number
 * is silently swept into a mean and still satisfies `skillBand <= 4`, inventing a novice that
 * does not exist. SQL skips NULL and comparisons against it return NULL. The REASON is kept in
 * `statsSource`, which is exactly what the self-selection analysis needs.
 */
export function getNerdleSkill(): NerdleSkill {
  // A handoff WINS OUTRIGHT, including g24Prior and targetsExposure. Those two read Targets
  // keys from this origin, and after an ejection from the app those keys are in the app's
  // store, not here — so a local read would not fail loudly, it would return the confident
  // wrong defaults '0' and 'new'. That is worse than measuring nothing, and is exactly what
  // the null discipline below exists to prevent. The only way a handoff exists is that the
  // participant was ejected from the app, where the real values were read.
  const handoff = readHandoff()
  if (handoff) return handoff

  const targets = readJSON('statsState_shuffle_numbers_targets1')
  let tgp = Number(targets?.gamesPlayed) || 0
  if (tgp <= 0 && Array.isArray(targets?.winRows)) {
    tgp = targets.winRows.reduce((a: number, b: number) => a + (Number(b) || 0), 0)
  }
  const base = { g24Prior: g24PriorBand(), targetsExposure: exposureBand(tgp) }
  const none = (why: string): NerdleSkill =>
    ({ skillBand: null, tenureBand: null, statsAgeBand: null, statsSource: why,
       lastGame: null, ...base })

  try {
    // `statsState` is the MAIN nerdle game's blob (same origin). `deviceStatsState` is its
    // device-local backup, which survives a cloud sync overwriting the primary for a
    // logged-in player, so it is the fallback rather than the first choice.
    let src = 'statsState'
    let st = readJSON('statsState')
    if (!st) {
      src = 'deviceStatsState'
      st = readJSON('deviceStatsState')
    }
    if (!st) return none('none')

    const wr: number[] = Array.isArray(st.winRows) ? st.winRows.map((x: any) => Number(x) || 0) : []
    const wins = wr.reduce((a, b) => a + b, 0)
    const gp = Number(st.gamesPlayed) || 0
    if (!wr.length || wins < MIN_GAMES || gp < MIN_GAMES) {
      // TENURE survives here; only SKILL needs the >=10 guard. A mean-guess estimate on <10 games
      // is noise, but `gamesPlayed = 4` is a perfectly good tenure measurement. Discarding it would
      // drop exactly the low-tenure players who are already under-represented (a device read fails
      // more often for them), so the bug compounded the skew rather than being neutral to it.
      return {
        skillBand: null,
        tenureBand: gp > 0 ? bandOf(gp, TENURE_CUTS) : null,
        statsAgeBand: ageBand(st.lastUpdated),
        statsSource: 'too-few-games',
        // lastGame survives the <10-games guard for the same reason tenure does: it is a
        // record of ONE game, not an estimate over many, so a low game count does not make
        // it noisy. Dropping it here would discard exactly the newest players.
        lastGame: readLastGame(st.lastGame),
        ...base,
      }
    }

    const meanGuess = wr.reduce((a, n, i) => a + (i + 1) * n, 0) / wins
    if (!isFinite(meanGuess) || meanGuess <= 0) return none('unparseable')

    return {
      skillBand: bandOf(meanGuess, SKILL_CUTS),
      tenureBand: bandOf(gp, TENURE_CUTS),
      statsAgeBand: ageBand(st.lastUpdated),
      statsSource: src,
      lastGame: readLastGame(st.lastGame),
      ...base,
    }
  } catch {
    return none('unparseable')
  }
}
